'use client';
import { NavigationCheck } from '@energy-retailer/icons';
import { clsx } from 'clsx';
import { useId } from 'react';

import { RequiredSpan } from '../required';

import {
  CheckboxVariant,
  CheckboxVerticalVariant,
  type CheckboxProps,
} from './checkbox.types';

const Checkbox = ({
  name,
  label,
  description,
  disabled,
  variant = CheckboxVariant.LEFT,
  verticalVariant = CheckboxVerticalVariant.CENTER,
  checked,
  required,
  onChange,
  hasError,
}: CheckboxProps) => {
  const uniqueId = useId();

  return (
    <div
      className={clsx(
        variant === CheckboxVariant.LEFT && 'items-start justify-start',
        'flex flex-col'
      )}
    >
      <div
        id={name}
        role="checkbox"
        aria-checked={checked ? 'true' : 'false'}
        tabIndex={disabled ? -1 : 0}
        aria-labelledby={`checkbox-label-${uniqueId}`}
        aria-describedby={`checkbox-description-${uniqueId}`}
        aria-required={required ? 'true' : 'false'}
        {...(disabled && { 'aria-disabled': disabled })}
        className={clsx(
          variant === CheckboxVariant.LEFT
            ? 'flex-row'
            : 'flex-row-reverse place-content-between justify-end',
          'group/checkbox relative flex cursor-pointer gap-b focus:outline-none aria-disabled:pointer-events-none',
          verticalVariant === CheckboxVerticalVariant.CENTER
            ? 'items-center'
            : 'items-start'
        )}
        onClick={() => {
          if (disabled) {
            return null;
          }
          onChange();
        }}
        onKeyDown={(evt) => {
          const isEnterOrSpace = evt.code === 'Enter' || evt.code === 'Space';
          if (disabled) {
            return;
          }
          if (isEnterOrSpace) {
            evt.preventDefault();
            onChange();
          }
        }}
      >
        <span
          aria-hidden="true"
          className={clsx(
            checked
              ? 'bg-checkbox-background-checked'
              : 'bg-checkbox-background-default',
            checked && disabled && '!bg-checkbox-background-disabled',
            'flex h-[1.5rem] w-[1.5rem] shrink-0 items-center justify-center rounded-rad-a border-2 border-checkbox-border-default transition-colors group-focus-visible/checkbox:border-checkbox-border-focus group-active/checkbox:border-checkbox-border-pressed group-active/checkbox:bg-checkbox-background-pressed group-aria-disabled/checkbox:border-checkbox-border-disabled',
            { 'border-error': hasError }
          )}
        >
          {checked ? (
            <NavigationCheck className="h-[1rem] w-[1rem] text-white" />
          ) : null}
        </span>
        <span
          className={clsx(
            variant === CheckboxVariant.RIGHT && 'flex-1',
            'flex flex-col'
          )}
        >
          <span
            id={`checkbox-label-${uniqueId}`}
            className="text-h4 block transition-colors group-hover/checkbox:text-checkbox-label-hover group-active/checkbox:text-checkbox-label-pressed group-aria-checked/checkbox:text-checkbox-label-checked group-aria-disabled/checkbox:text-checkbox-label-disabled"
          >
            {label} {required ? <RequiredSpan /> : null}
          </span>
          {description ? (
            <span
              id={`checkbox-description-${uniqueId}`}
              className="text-body2 block group-aria-disabled/checkbox:text-checkbox-label-disabled"
            >
              {description}
            </span>
          ) : null}
        </span>
      </div>
    </div>
  );
};

export default Checkbox;
